import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Check, Loader } from 'lucide-react';

const ThinkingIndicator = forwardRef(({ onComplete }, ref) => {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // Expose method to complete the thinking process externally
  useImperativeHandle(ref, () => ({
    complete: () => {
      if (currentPhraseIndex === thinkingPhrases.length - 1) {
        setIsComplete(true);
      }
    }
  }));


  const thinkingPhrases = useMemo(() => [
    { text: "Analizando tu negocio...", icon: "🔍" },
    { text: "Evaluando estrategia...", icon: "🎯" },
    { text: "Definiendo presupuesto...", icon: "💰" },
    { text: "Creando títulos y descripciones...", icon: "✍️" },
    { text: "Ultimando detalles...", icon: "⚙️" }
  ], []);

  useEffect(() => {
    if (isComplete) return;

    const currentPhrase = thinkingPhrases[currentPhraseIndex];
    
    if (currentText.length < currentPhrase.text.length) {
      // Efecto de escritura character by character
      const timeout = setTimeout(() => {
        setCurrentText(currentPhrase.text.slice(0, currentText.length + 1));
      }, 50); // Velocidad de escritura - 50ms por caracter
      
      return () => clearTimeout(timeout);
    } else if (currentText.length === currentPhrase.text.length && !showSpinner && !showCheck) {
      // Frase completa, mostrar spinner por 1 segundos
      setShowSpinner(true);
    }
  }, [currentText, currentPhraseIndex, showSpinner, showCheck, isComplete, thinkingPhrases]);

  // Separate effect for handling spinner to check transition
  useEffect(() => {
    if (showSpinner) {
      // Si estamos en la última frase "Ajustando detalles...", el spinner es infinito
      if (currentPhraseIndex === thinkingPhrases.length - 1) {
        // No hacer nada, el spinner permanece infinito hasta que se complete externamente
        return;
      }
      
      // Para las otras frases, timeout normal
      const timeout = setTimeout(() => {
        setShowSpinner(false);
        setShowCheck(true);
      }, 1000); // Mostrar spinner por 1 segundo
      
      return () => clearTimeout(timeout);
    }
  }, [showSpinner, currentPhraseIndex, thinkingPhrases.length]);

  // Separate effect for handling check to next phrase transition
  useEffect(() => {
    if (showCheck) {
      const timeout = setTimeout(() => {
        if (currentPhraseIndex < thinkingPhrases.length - 1) {
          setCurrentPhraseIndex(currentPhraseIndex + 1);
          setCurrentText('');
          setShowCheck(false);
          setShowSpinner(false);
        } else {
          // Llegamos a la última frase "Ajustando detalles..."
          // No llamamos onComplete aquí, la frase se mantiene con spinner
          setShowCheck(false);
          setShowSpinner(true);
        }
      }, 1000); // Mostrar check por 1 segundo antes de continuar
      
      return () => clearTimeout(timeout);
    }
  }, [showCheck, currentPhraseIndex, onComplete, thinkingPhrases.length]);

  // Effect para manejar cuando se debe completar externamente
  useEffect(() => {
    // Solo completar si estamos en la última frase y se llama onComplete externamente
    if (currentPhraseIndex === thinkingPhrases.length - 1 && isComplete) {
      if (onComplete) {
        onComplete();
      }
    }
  }, [isComplete, currentPhraseIndex, onComplete, thinkingPhrases.length]);

  if (isComplete) {
    return null;
  }

  return (
    <div className="flex items-end gap-1.5 sm:gap-2 mb-3 sm:mb-4 justify-start">
      {/* Avatar del AI */}
      <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-[#0F31F7] flex items-center justify-center flex-shrink-0">
        <img src="/isologo.blanco.png" alt="CH3" className="h-3 w-3 sm:h-4 sm:w-4 object-contain" />
      </div>

      {/* Contenedor del mensaje */}
      <div className="w-64 sm:w-72 px-3 py-2 sm:px-4 sm:py-3 shadow-sm bg-white rounded-2xl rounded-bl-md border border-gray-200 text-gray-800">
        <div className="flex items-center gap-3">
          <div className="text-lg animate-slideIn">
            {thinkingPhrases[currentPhraseIndex].icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="leading-relaxed font-medium text-gray-800 text-xs sm:text-sm h-5 sm:h-6 flex items-center">
              <span className="truncate">
                {currentText}
                {currentText.length < thinkingPhrases[currentPhraseIndex].text.length && (
                  <span className="animate-pulse ml-0.5 text-[#0F31F7] font-bold">|</span>
                )}
              </span>
            </div>
          </div>
          
          {showSpinner && (
            <div className="animate-fadeIn">
              <Loader className="w-4 h-4 text-[#0F31F7] animate-spin" />
            </div>
          )}
          
          {showCheck && (
            <div className="animate-fadeIn">
              <Check className="w-4 h-4 text-green-500" />
            </div>
          )}
        </div>
        
        {/* Indicador de progreso mejorado */}
        <div className="mt-3 space-y-1">
          <div className="flex justify-between text-[10px] text-gray-500">
            <span>Progreso</span>
            <span>{currentPhraseIndex + 1}/{thinkingPhrases.length}</span>
          </div>
          <div className="flex gap-1">
            {thinkingPhrases.map((phrase, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                  index < currentPhraseIndex 
                    ? 'bg-[#0F31F7] shadow-sm' 
                    : index === currentPhraseIndex 
                      ? 'bg-blue-300 shadow-md animate-pulse' 
                      : 'bg-gray-200'
                }`}
                title={phrase.text}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ThinkingIndicator;